
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  watch,
  Ref
} from "vue";
import { useRouter } from "vue-router";
import { formatDateTime } from "../../../modules/utils";
import {
  CATEGORIAS_HISTORICO,
  ORIGENS_OPERACAO,
  CategoriaHistorico,
  HistoricoLicencaPayload,
  OrigemOperacao,
  useLicencaService
} from "../../../modules/business/licenca/licencaBusiness";

interface HistoricoLicencaState {
  historico: Array<HistoricoLicencaPayload>;
  categorias: Array<CategoriaHistorico>;
  primeiraLinha: number;
  loadings: Array<Ref<boolean>>;
}

/**
 * Aba "Histórico" do cadastro da licença: quem fez o quê (cliente, robô,
 * monitoramento do órgão, rotinas), com antes e depois das trocas de veículo,
 * peso e dimensões. O monitoramento (consultas de situação) é a maior parte
 * das linhas; todas as categorias começam marcadas.
 */
export default defineComponent({
  props: {
    idLicenca: {
      type: Number,
      required: true
    },
    parentLoading: Boolean
  },
  setup(props) {
    const state = reactive<HistoricoLicencaState>({
      historico: [],
      categorias: ["CLIENTE", "ROBO", "MONITORAMENTO", "SISTEMA"],
      primeiraLinha: 0,
      loadings: []
    });

    const router = useRouter();

    const linhasPorPagina = 50;

    const categoriasDisponiveis = CATEGORIAS_HISTORICO;

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const getTempo = (linha: HistoricoLicencaPayload) =>
      linha.timestampOperacao ? new Date(linha.timestampOperacao).getTime() : 0;

    const carregarHistorico = () => {
      const { data, loading, getHistoricoLicenca } = useLicencaService();
      state.loadings.push(loading);
      // sem categoria marcada = todas
      getHistoricoLicenca(props.idLicenca, state.categorias)
        .then(() => {
          // mais recente primeiro; sort estavel: linhas da mesma alteracao
          // (mesmo horario) ficam na ordem gravada, com o alerta antes
          state.historico = [
            ...((data.value as Array<HistoricoLicencaPayload>) ?? [])
          ].sort((a, b) => getTempo(b) - getTempo(a));
          state.primeiraLinha = 0;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    watch(
      () => [...state.categorias],
      () => carregarHistorico()
    );

    const formatarData = (valor?: Date | string | null) => {
      if (!valor) {
        return "";
      }
      return formatDateTime(valor instanceof Date ? valor : new Date(valor));
    };

    const getDescricaoCategoria = (categoria: CategoriaHistorico) =>
      CATEGORIAS_HISTORICO.find((c) => c.categoria === categoria)?.descricao ??
      categoria;

    const getDescricaoOrigem = (origem: OrigemOperacao) =>
      ORIGENS_OPERACAO[origem] ?? origem;

    const getClasseLinha = (linha: HistoricoLicencaPayload) =>
      linha.flPosProtocolo ? "historico-pos-protocolo" : "";

    const getUrlLicenca = (idLicenca: number) =>
      router.resolve({
        name: "licenca-edit",
        params: { idLicenca: idLicenca + "" }
      }).href;

    onMounted(() => carregarHistorico());

    return {
      stateLoading,
      linhasPorPagina,
      categoriasDisponiveis,
      carregarHistorico,
      formatarData,
      getDescricaoCategoria,
      getDescricaoOrigem,
      getClasseLinha,
      getUrlLicenca,
      ...toRefs(state)
    };
  }
});
